import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useAxios } from "@/composables/api.js";
import { catchError } from "@/composables/error.js";
import { useAppStore } from "@/stores/app.js";
import { useCompanyStore } from "@/stores/company.js";

export const useCampaignStore = defineStore("campaign", () => {
  const appStore = useAppStore();

  const companyStore = useCompanyStore();

  const campaigns = ref([]);

  const archivedCampaigns = ref([]);

  const campaignsCalibrations = ref([]);

  const currentCampaign = computed(() => {
    if (!appStore.currentRoute.params.campaignId) return null;

    return (
      campaigns.value.find(
        (campaign) =>
          campaign.campaign_id === appStore.currentRoute.params.campaignId
      ) || null
    );
  });

  function isV2_1(campaign) {
    const date2_1 = new Date("2025-04-23T13:00Z"); // TODO env var ?
    let dateCandidateString = campaign.creation_time;
    // remove everything after the first "+"
    dateCandidateString = dateCandidateString.split("Z")[0];
    const dateCandidate = new Date(dateCandidateString);
    return dateCandidate > date2_1;
  }

  const currentCalibration = computed(() => {
    if (!currentCampaign.value || !currentCampaign.value.calibration_id)
      return null;

    return campaignsCalibrations.value.find(
      (calibration) =>
        calibration.calibration_id === currentCampaign.value.calibration_id
    );
  });

  const campaignShareLink = computed(() => {
    if (!currentCampaign.value || !currentCampaign.value.invitation_id)
      return null;

    return `${window.location.host.replace("app", "game")}/join?invitation_id=${
      currentCampaign.value.invitation_id
    }`;
  });

  async function createCampaign(companyId, workspaceId, campaign) {
    try {
      const { data } = await useAxios.post(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns`,
        campaign
      );

      campaigns.value.push(data.campaign);

      return data.campaign;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCampaign(companyId, workspaceId, campaignId) {
    try {
      const { data } = await useAxios.get(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}`
      );

      campaigns.value.push(data.campaign);

      return data.campaign;
    } catch (error) {
      this.$router.push("/error");

      return catchError(error);
    }
  }

  async function getCampaigns(companyId, workspaceId) {
    try {
      const { data } = await useAxios.get(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns`
      );

      data.campaigns.forEach((campaign) => {
        const alreadyIn = campaigns.value.find(
          (campaignObj) => campaignObj.campaign_id === campaign.campaign_id
        );

        if (!alreadyIn) campaigns.value = [...campaigns.value, campaign];
      });

      return data.campaigns;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updateCampaign(companyId, workspaceId, campaign) {
    try {
      const { data } = await useAxios.put(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaign.campaign_id}`,
        campaign
      );

      const campaignIndex = campaigns.value.findIndex(
        (campaignObj) => campaignObj.campaign_id === campaign.campaign_id
      );

      Object.assign(campaigns.value[campaignIndex], data.campaign);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCalibration(calibration_id) {
    try {
      const { data } = await useAxios.get(`/calibrations/${calibration_id}`);

      campaignsCalibrations.value.push(data.calibration);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updateCalibration(company_id, workspace_id, campaign_id) {
    try {
      const { data } = await useAxios.put(
        `/companies/${company_id}/workspaces/${workspace_id}/campaigns/${campaign_id}/calibration`
      );

      const calibrationIndex = campaignsCalibrations.value.findIndex(
        (calibrationObj) =>
          calibrationObj.calibration_id === data.calibration.calibration_id
      );

      if (calibrationIndex === -1) {
        campaignsCalibrations.value.push(data.calibration);
      }

      const campaignIndex = campaigns.value.findIndex(
        (campaign) => campaign.campaign_id === campaign_id
      );

      campaigns.value[campaignIndex].calibration_id =
        data.calibration.calibration_id;

      campaigns.value[campaignIndex].calibrationIsUpdated = true;

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updateCampaignInvitation(
    companyId,
    workspaceId,
    campaign_id,
    enabled_value
  ) {
    try {
      const invitationResp = await useAxios.put(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaign_id}/invitation`,
        {
          enabled: enabled_value,
        }
      );

      const campaignIndex = campaigns.value.findIndex(
        (campaignObj) => campaignObj.campaign_id === campaign_id
      );

      campaigns.value[campaignIndex].invitation_id =
        invitationResp.data.length !== 0
          ? invitationResp.data.invitation.invitation_id
          : null;
    } catch (error) {
      return catchError(error);
    }
  }

  async function archiveCampaign(companyId, workspaceId, campaignId) {
    try {
      const response = await useAxios.delete(
        `/companies/${companyId}/workspaces/${workspaceId}/campaigns/${campaignId}`
      );

      const index = campaigns.value.findIndex(
        (c) => c.campaign_id === campaignId
      );

      campaigns.value[index].archived = true;

      await companyStore.getCompanyCredits(companyId);

      return response;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getArchivedCampaigns(companyId) {
    try {
      const {
        data: { entries },
      } = await useAxios.get(`companies/${companyId}/archived-campaigns`);

      archivedCampaigns.value = entries;

      return entries;
    } catch (error) {
      return catchError(error);
    }
  }

  async function restoreCampaign(companyId, workspaceId, campaignId) {
    try {
      const response = await useAxios.put(
        `/companies/${companyId}/campaigns/${campaignId}/restore`,
        {
          workspace_id: workspaceId,
        }
      );

      const index = archivedCampaigns.value.findIndex(
        (elt) => elt.campaign.campaign_id === campaignId
      );

      const campaignObj = campaigns.value.find(
        (elt) => elt.campaign_id === campaignId
      );

      if (campaignObj) {
        campaignObj.workspace_id = workspaceId;
        campaignObj.archived = false;
      } else {
        archivedCampaigns.value[index].campaign.archived = false;
        archivedCampaigns.value[index].campaign.workspace_id = workspaceId;

        campaigns.value.push(archivedCampaigns.value[index].campaign);
      }

      archivedCampaigns.value.splice(index, 1);

      return response;
    } catch (error) {
      return catchError(error);
    }
  }

  function clear() {
    campaigns.value = [];
  }

  return {
    archivedCampaigns,
    campaigns,
    campaignsCalibrations,
    campaignShareLink,
    currentCalibration,
    currentCampaign,
    createCampaign,
    getCampaign,
    getCalibration,
    updateCalibration,
    getCampaigns,
    updateCampaign,
    updateCampaignInvitation,
    archiveCampaign,
    getArchivedCampaigns,
    restoreCampaign,
    clear,
    isV2_1,
  };
});
